
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import "./ChatInput.module.css";

const CHAT_BOX_BG = "bg-white";
const CHAT_BOX_BORDER = "border border-[#E7E3DA] dark:border-[#333333]"; 

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isProcessing: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isProcessing
}) => {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Refocus textarea when bot finishes responding
  useEffect(() => {
    if (!isProcessing) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isProcessing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isProcessing) {
      onSendMessage(message);
      setMessage("");
      // Keep focus on textarea after sending
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (message.trim() && !isProcessing) {
        onSendMessage(message);
        setMessage("");
        // Keep focus on textarea after sending
        setTimeout(() => textareaRef.current?.focus(), 0);
      }
    }
    // If Shift+Enter, allow default (newline)
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="fixed bottom-4 left-0 w-full flex justify-center px-0 bg-[#F4F1ED] z-30"
    >
      <div
        className={[
          "relative w-full max-w-2xl flex items-end mx-4",
          CHAT_BOX_BG,
          CHAT_BOX_BORDER,
          "rounded-2xl shadow-sm",
        ].join(" ")}
      >
        <textarea
          ref={textareaRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Ask anything"
          disabled={isProcessing}
          rows={3}
          className="w-full h-20 pl-4 pr-14 pt-4 pb-4 resize-none bg-transparent outline-none focus:ring-0 focus:outline-none rounded-2xl placeholder-top placeholder-gray-400 text-base"
          style={{
            minHeight: "80px",
            maxHeight: "150px",
            overflow: "auto",
            lineHeight: "1.5"
          }}
          onKeyDown={handleKeyDown}
        />
        <Button
          type="submit"
          size="icon"
          disabled={isProcessing || !message.trim()}
          className="absolute bottom-2 right-2 bg-black hover:bg-black/90 text-white rounded-full shadow-none disabled:bg-[#B8D0D4]"
          tabIndex={-1}
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>
    </form>
  );
};

export default ChatInput;

